import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import FormInput, { FormSelect } from '../../features/setup/components/FormInput';

const US_STATES = [
  { value: '', label: 'Select state...' },
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface SelectOption {
  value: string;
  label: string;
}

export interface AddressData {
  street1: string;
  street2?: string;
  streetSuffix?: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  country?: string;
}

interface AddressFieldsProps {
  brand: BrandConfig;
  prefix: string;
  value: AddressData;
  onChange: (address: AddressData) => void;
  required?: boolean;
  showStreet2?: boolean;
  showCounty?: boolean;
  showStreetSuffix?: boolean;
  streetSuffixOptions?: SelectOption[];
  showCountry?: boolean;
  statesOptions?: SelectOption[];
  street1Label?: string;
  street2Label?: string;
}

const AddressFields: React.FC<AddressFieldsProps> = ({
  brand,
  prefix,
  value,
  onChange,
  required = false,
  showStreet2 = true,
  showCounty = false,
  showStreetSuffix = false,
  streetSuffixOptions = [],
  showCountry = false,
  statesOptions = US_STATES,
  street1Label = 'Street Address',
  street2Label = 'Street Address 2',
}) => {
  const update = (field: keyof AddressData, val: string) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <div>
      <FormInput
        brand={brand}
        label={street1Label}
        name={`${prefix}-street1`}
        value={value.street1}
        onChange={(e) => update('street1', e.target.value)}
        required={required}
        placeholder="123 Main St"
      />
      {showStreet2 && (
        <FormInput
          brand={brand}
          label={street2Label}
          name={`${prefix}-street2`}
          value={value.street2 ?? ''}
          onChange={(e) => update('street2', e.target.value)}
          placeholder="Suite 100"
        />
      )}
      {showStreetSuffix && (
        <FormSelect
          brand={brand}
          label="Street Suffix"
          name={`${prefix}-streetSuffix`}
          value={value.streetSuffix ?? ''}
          onChange={(e) => update('streetSuffix', e.target.value)}
          options={streetSuffixOptions}
        />
      )}
      <div className="grid grid-cols-2 gap-3">
        <FormInput
          brand={brand}
          label="City"
          name={`${prefix}-city`}
          value={value.city}
          onChange={(e) => update('city', e.target.value)}
          required={required}
        />
        <FormSelect
          brand={brand}
          label="State"
          name={`${prefix}-state`}
          value={value.state}
          onChange={(e) => update('state', e.target.value)}
          options={statesOptions}
          required={required}
        />
      </div>
      <div className={showCounty ? 'grid grid-cols-2 gap-3' : ''}>
        <FormInput
          brand={brand}
          label="ZIP Code"
          name={`${prefix}-zip`}
          value={value.zip}
          onChange={(e) => update('zip', e.target.value)}
          required={required}
          placeholder="90001"
        />
        {showCounty && (
          <FormInput
            brand={brand}
            label="County"
            name={`${prefix}-county`}
            value={value.county ?? ''}
            onChange={(e) => update('county', e.target.value)}
          />
        )}
      </div>
      {showCountry && (
        <FormInput
          brand={brand}
          label="Country"
          name={`${prefix}-country`}
          value={value.country ?? ''}
          onChange={(e) => update('country', e.target.value)}
        />
      )}
    </div>
  );
};

export default AddressFields;
export { US_STATES };
